/**
 * Export Customer Invoice Modal
 * Allows admin to select and export assignments ready for customer invoicing
 * Combines reported articles from all TGs on each assignment
 */

import { useState, useMemo } from 'react'
import { X, Download, FileSpreadsheet, MapPin, Check, Loader2, Info, User } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '../lib/supabase'
import { generateCustomerInvoiceExcel, type CustomerInvoiceExportAssignment, type CombinedArticle, type TGReport } from '../services/customerInvoiceExport'
import * as XLSX from 'xlsx'
import toast from 'react-hot-toast'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  customer_id: string
  customer_name: string
  location: string | null
}

interface ExportCustomerInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: Assignment[]
  onExportComplete?: () => void
}

export function ExportCustomerInvoiceModal({
  isOpen,
  onClose,
  assignments,
  onExportComplete
}: ExportCustomerInvoiceModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
    new Set(assignments.map(a => a.id))
  )
  const [isExporting, setIsExporting] = useState(false)

  // Group assignments by customer
  const groupedAssignments = useMemo(() => {
    const groups: Record<string, { customer_name: string; assignments: Assignment[] }> = {}
    for (const assignment of assignments) {
      if (!groups[assignment.customer_id]) {
        groups[assignment.customer_id] = {
          customer_name: assignment.customer_name,
          assignments: []
        }
      }
      groups[assignment.customer_id].assignments.push(assignment)
    }
    // Sort groups by customer name
    return Object.entries(groups).sort(([, a], [, b]) =>
      a.customer_name.localeCompare(b.customer_name)
    )
  }, [assignments])

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(assignments.map(a => a.id)))
  }

  const selectNone = () => {
    setSelectedIds(new Set())
  }

  const handleExport = async () => {
    if (selectedIds.size === 0) return

    setIsExporting(true)

    try {
      const selectedAssignmentIds = Array.from(selectedIds)

      // Fetch all TG reports for selected assignments
      const { data: tgData, error: tgError } = await supabase
        .from('assignment_subcontractors')
        .select(`
          assignment_id,
          subcontractor_id,
          report_text,
          reported_articles,
          subcontractors (id, name, price_list_id)
        `)
        .in('assignment_id', selectedAssignmentIds)
        .eq('tg_status', 'godkand')

      if (tgError) throw tgError

      // Get unique customer IDs to fetch their price lists
      const selectedAssignments = assignments.filter(a => selectedIds.has(a.id))
      const customerIds = [...new Set(selectedAssignments.map(a => a.customer_id))]

      // Fetch customer price lists, RUT status, and Fortnox ID
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('id, price_list_id, wants_rut, fortnox_customer_id')
        .in('id', customerIds)

      if (customerError) throw customerError

      // Build customer price list map, RUT status map, and Fortnox ID map
      const customerPriceListMap: Record<string, string | null> = {}
      const customerWantsRutMap: Record<string, boolean> = {}
      const customerFortnoxIdMap: Record<string, string | null> = {}
      for (const customer of customerData || []) {
        customerPriceListMap[customer.id] = customer.price_list_id
        customerWantsRutMap[customer.id] = customer.wants_rut || false
        customerFortnoxIdMap[customer.id] = customer.fortnox_customer_id || null
      }

      // Fetch all unique price list IDs
      const priceListIds = [...new Set(Object.values(customerPriceListMap).filter(Boolean))] as string[]

      // Fetch prices from all relevant price lists
      const pricesByList: Record<string, Record<string, number>> = {}
      if (priceListIds.length > 0) {
        const { data: priceItems, error: priceError } = await supabase
          .from('price_list_articles')
          .select('price_list_id, article_id, price_excl_vat')
          .in('price_list_id', priceListIds)

        if (priceError) throw priceError

        for (const item of priceItems || []) {
          if (!pricesByList[item.price_list_id]) {
            pricesByList[item.price_list_id] = {}
          }
          pricesByList[item.price_list_id][item.article_id] = item.price_excl_vat
        }
      }

      // Fetch assignment details (including admin_reported_articles)
      const { data: assignmentDetails, error: assignmentError } = await supabase
        .from('assignments')
        .select('id, assignment_number, title, start_date, location, customer_id, admin_reported_articles')
        .in('id', selectedAssignmentIds)

      if (assignmentError) throw assignmentError

      // Collect all unique article IDs from reported articles to fetch RUT status
      const articleIds = new Set<string>()
      for (const tg of tgData || []) {
        const articles = (tg.reported_articles as any[]) || []
        for (const article of articles) {
          if (article.article_id && !article.is_expense) {
            articleIds.add(article.article_id)
          }
        }
      }
      // Also collect from admin_reported_articles
      for (const assignment of assignmentDetails || []) {
        const adminArticles = (assignment.admin_reported_articles as any[]) || []
        for (const article of adminArticles) {
          if (article.article_id && !article.is_expense) {
            articleIds.add(article.article_id)
          }
        }
      }

      // Fetch article RUT status
      const articleRutMap: Record<string, boolean> = {}
      if (articleIds.size > 0) {
        const { data: articlesData, error: articlesError } = await supabase
          .from('articles')
          .select('id, is_rut_applicable')
          .in('id', Array.from(articleIds))

        if (articlesError) throw articlesError

        for (const article of articlesData || []) {
          articleRutMap[article.id] = article.is_rut_applicable || false
        }
      }

      // Group TG data by assignment
      const tgByAssignment: Record<string, typeof tgData> = {}
      for (const tg of tgData || []) {
        if (!tgByAssignment[tg.assignment_id]) {
          tgByAssignment[tg.assignment_id] = []
        }
        tgByAssignment[tg.assignment_id].push(tg)
      }

      // Build a map of assignment_id to customer info from props
      const assignmentCustomerMap: Record<string, { customer_id: string; customer_name: string }> = {}
      for (const a of assignments) {
        assignmentCustomerMap[a.id] = { customer_id: a.customer_id, customer_name: a.customer_name }
      }

      // Build export data
      const exportData: CustomerInvoiceExportAssignment[] = (assignmentDetails || []).map(assignment => {
        const tgs = tgByAssignment[assignment.id] || []
        const customerPriceListId = customerPriceListMap[assignment.customer_id]
        const priceMap = customerPriceListId ? pricesByList[customerPriceListId] || {} : {}
        const customerInfo = assignmentCustomerMap[assignment.id] || { customer_id: assignment.customer_id, customer_name: 'Okänd kund' }
        const customerWantsRut = customerWantsRutMap[assignment.customer_id] || false

        // Combine articles from all TGs (handle expenses separately)
        const articleMap: Record<string, CombinedArticle> = {}
        const expenses: CombinedArticle[] = []

        for (const tg of tgs) {
          const articles = (tg.reported_articles as any[]) || []
          for (const article of articles) {
            // Utlägg ska inte kombineras - lägg till separat
            if (article.is_expense) {
              expenses.push({
                article_id: null,
                article_name: article.article_name,
                total_quantity: 1,
                unit: 'st',
                unit_price: null,
                notes: article.notes || undefined,
                source: 'tg',
                is_expense: true,
                expense_amount_incl_vat: article.expense_amount_incl_vat || null,
                expense_amount_excl_vat: article.expense_amount_excl_vat || null,
                expense_vat_rate: article.expense_vat_rate ?? null
              })
              continue
            }

            const key = article.article_id
            if (!articleMap[key]) {
              articleMap[key] = {
                article_id: article.article_id,
                article_name: article.article_name,
                total_quantity: 0,
                unit: article.unit || null,
                unit_price: priceMap[article.article_id] || null,
                is_rut_applicable: articleRutMap[article.article_id] || false,
                notes: article.notes || undefined,
                source: 'tg'
              }
            }
            articleMap[key].total_quantity += article.actual_quantity || 0
            // Combine notes if multiple
            if (article.notes && !articleMap[key].notes?.includes(article.notes)) {
              articleMap[key].notes = articleMap[key].notes
                ? `${articleMap[key].notes}; ${article.notes}`
                : article.notes
            }
          }
        }

        // Add admin/central reported articles
        const adminArticles = (assignment.admin_reported_articles as any[]) || []
        for (const article of adminArticles) {
          if (article.is_expense) {
            expenses.push({
              article_id: null,
              article_name: article.article_name,
              total_quantity: 1,
              unit: 'st',
              unit_price: null,
              notes: article.notes || undefined,
              source: 'central',
              is_expense: true,
              expense_amount_incl_vat: article.expense_amount_incl_vat || null,
              expense_amount_excl_vat: article.expense_amount_excl_vat || null,
              expense_vat_rate: article.expense_vat_rate ?? null
            })
            continue
          }

          // Central articles are kept separate (not combined with TG articles)
          // since they come from a different source
          const centralKey = `central_${article.article_id}`
          if (!articleMap[centralKey]) {
            articleMap[centralKey] = {
              article_id: article.article_id,
              article_name: article.article_name,
              total_quantity: 0,
              unit: article.unit || null,
              unit_price: article.article_id ? (priceMap[article.article_id] || null) : null,
              is_rut_applicable: article.article_id ? (articleRutMap[article.article_id] || false) : false,
              notes: article.notes || undefined,
              source: 'central'
            }
          }
          articleMap[centralKey].total_quantity += article.actual_quantity || 0
          if (article.notes && !articleMap[centralKey].notes?.includes(article.notes)) {
            articleMap[centralKey].notes = articleMap[centralKey].notes
              ? `${articleMap[centralKey].notes}; ${article.notes}`
              : article.notes
          }
        }

        // Collect TG reports
        const tgReports: TGReport[] = tgs
          .filter(tg => tg.report_text)
          .map(tg => ({
            tg_name: (tg.subcontractors as any)?.name || 'Okänd TG',
            report_text: tg.report_text || ''
          }))

        return {
          id: assignment.id,
          assignment_number: assignment.assignment_number,
          title: assignment.title,
          start_date: assignment.start_date,
          location: typeof assignment.location === 'string'
            ? assignment.location
            : (assignment.location as any)?.address || null,
          customer_id: customerInfo.customer_id,
          customer_name: customerInfo.customer_name,
          customer_wants_rut: customerWantsRut,
          fortnox_customer_id: customerFortnoxIdMap[assignment.customer_id] || null,
          combined_articles: [...Object.values(articleMap), ...expenses],
          tg_reports: tgReports
        }
      })

      // Sort by date
      exportData.sort((a, b) => a.start_date.localeCompare(b.start_date))

      // Generate and download Excel
      const workbook = generateCustomerInvoiceExcel(exportData)
      const dateStr = format(new Date(), 'yyyy-MM-dd')
      const filename = `kundfakturering-${dateStr}.xlsx`
      XLSX.writeFile(workbook, filename)

      toast.success(`${selectedIds.size} uppdrag exporterade`)

      onExportComplete?.()
      onClose()
    } catch (err) {
      console.error('Export error:', err)
      toast.error('Kunde inte exportera data')
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-nature-800 rounded-xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Exportera kundfakturering
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <X className="h-5 w-5 text-nature-500" />
          </button>
        </div>

        {/* Info box */}
        <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Kundfakturering</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-300 text-xs">
                <li>Visar uppdrag där alla TG:s har rapporterat (godkänt)</li>
                <li>Artiklar från alla TG:s kombineras per uppdrag</li>
                <li>Priser hämtas från kundens prislista</li>
                <li>Markera som fakturerad görs på uppdragssidan</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-amber-50 dark:bg-amber-900/20">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            <strong>OBS:</strong> Detta är ett automatiskt genererat underlag. Kontrollera att uppgifterna stämmer innan du fakturerar.
          </p>
        </div>

        {/* Selection controls */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-nature-600 dark:text-nature-400">
              {selectedIds.size} av {assignments.length} valda
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Välj alla
              </button>
              <button
                onClick={selectNone}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Avmarkera alla
              </button>
            </div>
          </div>
        </div>

        {/* Assignment list grouped by customer */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {assignments.length === 0 ? (
            <p className="text-center text-nature-500 dark:text-nature-400 py-8">
              Inga uppdrag redo för kundfakturering.
            </p>
          ) : (
            groupedAssignments.map(([customerId, { customer_name, assignments: customerAssignments }]) => (
              <div key={customerId}>
                {/* Customer header */}
                <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-2 flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {customer_name}
                </h3>

                {/* Assignments for this customer */}
                <div className="space-y-2">
                  {customerAssignments.map(assignment => {
                    const isSelected = selectedIds.has(assignment.id)
                    return (
                      <button
                        key={assignment.id}
                        onClick={() => toggleSelect(assignment.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-nature-300 dark:border-nature-600'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>

                          {/* Assignment info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                              {assignment.assignment_number && (
                                <span className="text-nature-500 dark:text-nature-400 mr-1">
                                  #{assignment.assignment_number}
                                </span>
                              )}
                              {assignment.title}
                            </h4>

                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-nature-500 dark:text-nature-400">
                              <span>{assignment.start_date}</span>
                              {assignment.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{assignment.location}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleExport}
            disabled={selectedIds.size === 0 || isExporting}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Exporterar...' : `Exportera ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
